import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Radio, Send, Save, Wifi, WifiOff, BookOpen, Copy } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EVENT_ROWS = [
  { flag: "mqtt_evt_created",   label: "booking.created",   help: "New booking received" },
  { flag: "mqtt_evt_updated",   label: "booking.updated",   help: "Reschedule / crew reassign / notes change" },
  { flag: "mqtt_evt_cancelled", label: "booking.cancelled", help: "Guest or admin cancels" },
  { flag: "mqtt_evt_deleted",   label: "booking.deleted",   help: "Hard delete from the calendar" },
  { flag: "mqtt_evt_reminder",  label: "booking.reminder",  help: "X minutes before start (uses reminder worker)" },
  { flag: "mqtt_evt_started",   label: "booking.started",   help: "Start time reached — fires within ~60s" },
  { flag: "mqtt_evt_ended",     label: "booking.ended",     help: "End time reached — fires within ~60s" },
];

const FIELD_ROWS = [
  { flag: "mqtt_field_booking_id", label: "Booking ID" },
  { flag: "mqtt_field_guest_name", label: "Guest name" },
  { flag: "mqtt_field_cabin",      label: "Cabin name" },
  { flag: "mqtt_field_treatment",  label: "Treatment name" },
  { flag: "mqtt_field_therapist",  label: "Therapist name" },
  { flag: "mqtt_field_start_at",   label: "Start time (ISO)" },
  { flag: "mqtt_field_end_at",     label: "End time (ISO)" },
  { flag: "mqtt_field_duration",   label: "Duration (min)" },
  { flag: "mqtt_field_notes",      label: "Notes" },
  { flag: "mqtt_field_status",     label: "Status" },
];

// Map each payload-field flag → the JSON key it produces + a realistic sample.
// The TopicReference block below uses this so the developer sees exactly the
// shape a subscriber will receive with the CURRENT toggles.
const FIELD_SAMPLES = {
  mqtt_field_booking_id:  { key: "id",             sample: "6621f0e2a5b3c412f8a71234" },
  mqtt_field_guest_name:  { key: "guest_name",     sample: "Alice Doe" },
  mqtt_field_cabin:       { key: "cabin_name",     sample: "Owner" },
  mqtt_field_treatment:   { key: "treatment_name", sample: "Signature Massage" },
  mqtt_field_therapist:   { key: "therapist_name", sample: "Ivo" },
  mqtt_field_start_at:    { key: "start_at",       sample: "2026-02-15T10:00:00+00:00" },
  mqtt_field_end_at:      { key: "end_at",         sample: "2026-02-15T11:00:00+00:00" },
  mqtt_field_duration:    { key: "duration_min",   sample: 60 },
  mqtt_field_notes:       { key: "notes",          sample: "Prefers lavender oil" },
  mqtt_field_status:      { key: "status",         sample: "confirmed" },
};

function TopicReference({ form }) {
  const prefix = (form.mqtt_topic_prefix || `slx/${form.vessel_id || "vessel"}`).replace(/^\/+|\/+$/g, "");
  const samplePayload = useMemo(() => {
    const p = { event: "booking.created", at: "2026-02-14T09:00:00+00:00" };
    if ((form.mqtt_message_prefix || "").trim()) p.prefix = form.mqtt_message_prefix;
    for (const [flag, meta] of Object.entries(FIELD_SAMPLES)) {
      if (form[flag]) p[meta.key] = meta.sample;
    }
    return p;
  }, [form]);
  const samplePretty = JSON.stringify(samplePayload, null, 2);
  const copySample = async () => {
    try { await navigator.clipboard.writeText(samplePretty); toast.success("Sample payload copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="border border-accent/30 bg-accent/5 p-4" data-testid="mqtt-topic-reference">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="overline text-accent flex items-center gap-2">
            <BookOpen className="h-3 w-3" /> Topic reference · dev docs
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Every topic this platform will publish to, given your current prefix. Subscribe to
            <span className="font-mono"> {prefix}/reservations/#</span> to catch them all, or filter
            by event topic. Payloads are UTF-8 JSON, QoS 1, not retained.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={copySample} className="text-xs" data-testid="mqtt-copy-sample">
          <Copy className="h-3 w-3 mr-1.5" /> Copy sample payload
        </Button>
      </div>

      <div className="border border-border overflow-x-auto bg-background">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="p-2 font-normal overline text-primary/50">Event</th>
              <th className="p-2 font-normal overline text-primary/50">Topic</th>
              <th className="p-2 font-normal overline text-primary/50">Fires when</th>
              <th className="p-2 font-normal overline text-primary/50 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {EVENT_ROWS.map((r) => {
              const event = r.label.replace("booking.", "");
              const topic = `${prefix}/reservations/${event}`;
              const active = !!form[r.flag];
              return (
                <tr key={r.flag} className="border-t border-border" data-testid={`mqtt-topic-row-${event}`}>
                  <td className="p-2 font-mono">{r.label}</td>
                  <td className="p-2 font-mono text-accent break-all">{topic}</td>
                  <td className="p-2 text-muted-foreground">{r.help}</td>
                  <td className="p-2 text-right">
                    {active
                      ? <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">On</span>
                      : <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider bg-muted text-muted-foreground border border-border">Off</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <div className="overline text-primary/50 text-[10px] mb-1">Sample payload · booking.created</div>
        <pre
          className="text-[11px] font-mono bg-background border border-border p-3 overflow-x-auto max-h-72"
          data-testid="mqtt-sample-payload"
        >{samplePretty}</pre>
        <p className="text-[11px] text-muted-foreground mt-2">
          Fields reflect your current payload toggles above — turn one on/off and this preview updates instantly.
          Missing fields on a real booking (e.g. no <span className="font-mono">notes</span>) are omitted rather than sent as <span className="font-mono">null</span>.
        </p>
      </div>

      <div className="mt-4 text-[11px] text-muted-foreground space-y-1">
        <div><strong className="text-primary">Test topic:</strong> <span className="font-mono">{prefix}/test</span> — used by the "Send test message" button below.</div>
        <div><strong className="text-primary">Dedupe flags:</strong> <span className="font-mono">booking.started</span> and <span className="font-mono">booking.ended</span> are marked <span className="font-mono">mqtt_started_sent</span> / <span className="font-mono">mqtt_ended_sent</span> on each booking so they fire exactly once even if the reminder worker restarts.</div>
        <div><strong className="text-primary">Delivery guarantee:</strong> QoS 1 (at-least-once). Configure your broker with a durable session if you need offline queue-and-forward.</div>
      </div>
    </div>
  );
}

export default function MqttPanel() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform/mqtt/settings");
      setForm(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load MQTT settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/platform/mqtt/settings", form);
      setForm(data);
      toast.success("MQTT settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/platform/mqtt/test", { topic_suffix: "test" });
      if (data?.ok) {
        toast.success(data.detail || `Published to ${data.topic}`);
      } else {
        toast.error(data?.detail || "MQTT test failed", { duration: 8000 });
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Test send failed", { duration: 8000 });
    } finally { setTesting(false); }
  };

  if (loading || !form) return <div className="text-sm text-muted-foreground">Loading MQTT settings…</div>;

  const enabled = !!form.mqtt_enabled;
  const previewTopic = `${(form.mqtt_topic_prefix || `slx/${form.vessel_id || "vessel"}`).replace(/^\/+|\/+$/g, "")}/reservations/created`;

  return (
    <section className="border border-border bg-card" data-testid="mqtt-panel">
      <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent flex items-center gap-2">
            <Radio className="h-3 w-3" /> MQTT push notifications
          </div>
          <h3 className="font-display text-xl mt-1">Broker integration</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Push each booking event to your own MQTT broker. QoS 1, not retained. Developer-only.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs flex items-center gap-1 ${enabled ? "text-emerald-500" : "text-muted-foreground"}`}>
            {enabled ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => patch({ mqtt_enabled: !!v })}
            data-testid="mqtt-master-toggle"
          />
        </div>
      </div>

      <div className={`p-6 space-y-6 ${enabled ? "" : "opacity-60 pointer-events-none"}`}>
        {/* Connection */}
        <div>
          <div className="overline text-primary/50 mb-3">Broker connection</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline text-primary/60">Host</Label>
              <Input value={form.mqtt_host || ""} onChange={(e) => patch({ mqtt_host: e.target.value })}
                     placeholder="broker.hivemq.com" data-testid="mqtt-host" className="mt-2" />
            </div>
            <div>
              <Label className="overline text-primary/60">Port</Label>
              <Input type="number" value={form.mqtt_port || 1883}
                     onChange={(e) => patch({ mqtt_port: parseInt(e.target.value || "1883", 10) })}
                     data-testid="mqtt-port" className="mt-2" />
            </div>
            <div>
              <Label className="overline text-primary/60">Username</Label>
              <Input value={form.mqtt_username || ""} onChange={(e) => patch({ mqtt_username: e.target.value })}
                     data-testid="mqtt-username" className="mt-2" />
            </div>
            <div>
              <Label className="overline text-primary/60">Password</Label>
              <Input type="password" value={form.mqtt_password || ""}
                     onChange={(e) => patch({ mqtt_password: e.target.value })}
                     data-testid="mqtt-password" className="mt-2" />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!form.mqtt_use_tls} onCheckedChange={(v) => patch({ mqtt_use_tls: !!v })}
                      data-testid="mqtt-tls" />
              <Label className="text-sm">Use TLS (port 8883)</Label>
            </div>
            <div>
              <Label className="overline text-primary/60">Client ID prefix</Label>
              <Input value={form.mqtt_client_id_prefix || "slx-one"}
                     onChange={(e) => patch({ mqtt_client_id_prefix: e.target.value })}
                     data-testid="mqtt-client-id" className="mt-2" />
            </div>
          </div>
        </div>

        {/* Topic + message prefix */}
        <div>
          <div className="overline text-primary/50 mb-3">Topics & messages</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline text-primary/60">Vessel ID</Label>
              <Input value={form.vessel_id || "vessel"}
                     onChange={(e) => patch({ vessel_id: e.target.value })}
                     placeholder="my-yacht" data-testid="mqtt-vessel-id" className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1">Stable namespace ID. Keep it unchanged if the yacht display name changes.</p>
            </div>
            <div>
              <Label className="overline text-primary/60">Topic prefix override <span className="normal-case tracking-normal">(optional)</span></Label>
              <Input value={form.mqtt_topic_prefix || ""}
                     onChange={(e) => patch({ mqtt_topic_prefix: e.target.value })}
                     placeholder={`slx/${form.vessel_id || "vessel"}`}
                     data-testid="mqtt-topic-prefix" className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1 font-mono" data-testid="mqtt-topic-preview">→ {previewTopic}</p>
            </div>
            <div>
              <Label className="overline text-primary/60">Message prefix (optional)</Label>
              <Input value={form.mqtt_message_prefix || ""}
                     onChange={(e) => patch({ mqtt_message_prefix: e.target.value })}
                     placeholder="e.g. [Carinthia VII]"
                     data-testid="mqtt-message-prefix" className="mt-2" />
              <p className="text-xs text-muted-foreground mt-1">Added as <code>prefix</code> in every payload.</p>
            </div>
          </div>
        </div>

        {/* Events */}
        <div>
          <div className="overline text-primary/50 mb-3">Events to publish</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {EVENT_ROWS.map((r) => (
              <label key={r.flag} className="flex items-start gap-3 border border-border p-3 cursor-pointer hover:bg-muted/30"
                     data-testid={`mqtt-evt-row-${r.flag}`}>
                <Switch checked={!!form[r.flag]} onCheckedChange={(v) => patch({ [r.flag]: !!v })}
                        data-testid={`mqtt-${r.flag}`} />
                <div className="flex-1">
                  <div className="font-mono text-sm">{r.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.help}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Payload fields */}
        <div>
          <div className="overline text-primary/50 mb-3">Payload fields</div>
          <p className="text-xs text-muted-foreground mb-3">
            Pick what goes into every published JSON payload. Guest name + cabin default ON.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {FIELD_ROWS.map((f) => (
              <label key={f.flag} className="flex items-center gap-2 border border-border p-2 cursor-pointer hover:bg-muted/30"
                     data-testid={`mqtt-field-row-${f.flag}`}>
                <Switch checked={!!form[f.flag]} onCheckedChange={(v) => patch({ [f.flag]: !!v })}
                        data-testid={`mqtt-${f.flag}`} />
                <span className="text-sm">{f.label}</span>
              </label>
            ))}
          </div>
        </div>

        <TopicReference form={form} />

        <div className="flex flex-wrap gap-2 border-t border-border pt-6">
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="mqtt-save">
            <Save className="h-3.5 w-3.5 mr-2" /> {saving ? "Saving…" : "Save MQTT settings"}
          </Button>
          <Button variant="outline" onClick={testConn} disabled={testing || !form.mqtt_host}
                  className="rounded-full border-accent/40 text-accent hover:bg-accent/5"
                  data-testid="mqtt-test">
            <Send className="h-3.5 w-3.5 mr-2" /> {testing ? "Testing…" : "Send test message"}
          </Button>
        </div>
      </div>
    </section>
  );
}
